import { useCallback, useContext, useState } from "react";
import { AuthContext, AuthContextValue } from "./AuthProvider";
import { ApiError, toApiError } from "../network/mainClient/apiError";
import { TFactor } from "../models/Factors";

type VerifyFactorResult = {
  authSessionId: string;
  session: {
    accessToken: string;
    refreshToken: string;
    created: string;
    expiry: string;
  };
  nextFactor?: TFactor;
};

export const useVerifyFactor = (): [
  VerifyFactorResult | null,
  boolean,
  ApiError | null,
  (
    authSessionId: string,
    identity: string,
    factorId: string,
    code: string
  ) => void
] => {
  const { verifyFactor } = useContext<AuthContextValue>(AuthContext);
  const [result, setResult] = useState<VerifyFactorResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const request = useCallback(
    async (
      authSessionId: string,
      identity: string,
      factorId: string,
      code: string
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await verifyFactor(
          authSessionId,
          identity,
          factorId,
          code
        );
        setResult(response);
      } catch (e) {
        setError(toApiError(e));
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [verifyFactor]
  );

  return [result, isLoading, error, request];
};
